/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Side panel — Figma node 3281:98498 (chat home).
// Width 339, full height. Background `surface-panel`, border-right
// `border-subtle`. Renders four stacked sections (panel header,
// optional "Your Companies", filter chips, scrollable chat list).
// Every colour comes from the design-token classes
// (`light-*` / `dark-*`); inline `style` is reserved for things
// Tailwind can't express (size-specific border colour reads,
// dimensions that don't align to the radius scale, etc.).

import {
  ArchiveRestore,
  Bell,
  Pin,
  PinOff,
  Check,
  ChevronRight,
  type LucideIcon,
  Folder,
  FolderPlus,
  BellOff,
  MessageCirclePlus,
  PhoneCall,
  Plus,
  Trash2,
  Users,
  Video,
  X,
} from "lucide-react-native";
import { NewChatIcon } from "./NewChatIcon";
import { ArchiveGlyph } from "./ArchiveGlyph";
import { MessagePreview } from "./MessagePreview";
import { peekComposerDraft } from "./composer/composerDraftStore";
import { TabView, type TabViewRoute } from "../components/TabView";
import { AnchoredPopover } from "../components/AnchoredPopover";
import type React from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FlatList, type ListRenderItem, Pressable, ScrollView, Text, View } from 'react-native';
import { useColorScheme } from '../foundations/colorScheme';
import { Avatar } from "../components/Avatar";
import { VerifiedBadge } from "../components/VerifiedBadge";
import { tokens } from "../design-system/tokens";
import {
  SIDE_PANEL_MAX_WIDTH,
  SIDE_PANEL_MIN_WIDTH,
  useUiPreferencesStore,
} from "../preferences/uiPreferencesStore";
import {
  RECENT_JOIN_WINDOW_MS,
  useChatSyncStore,
} from "../sync/chatSyncStore";
import type {
  ChatConversationRow,
  CompanyRow,
  ConversationFilterTab,
  ConversationListPanelProps,
} from "./ChatRoom.types";

const DEFAULT_FILTER_TABS: readonly ConversationFilterTab[] = [
  { id: "all", label: "All", leadingIcon: "all" },
  { id: "unread", label: "Unread" },
  { id: "favourites", label: "Favourites", leadingIcon: "star" },
  { id: "dm", label: "DM" },
];

// Active vs Archived panes, driven through the shared <TabView> so both stay
// mounted (no list refresh on switch) with an animated slide between them.
const CONVERSATION_TAB_ROUTES: readonly TabViewRoute[] = [
  { key: "active" },
  { key: "archived" },
];

// A conversation is archived when it carries a positive `archivedAt` marker.
// Archived rows are hidden from the normal filter tabs and surfaced only under
// the leading Archive tab.
function isArchivedRow(c: ChatConversationRow): boolean {
  return (c.archivedAt ?? 0) > 0;
}

// A conversation is pinned when it carries a positive `pinnedAt` marker.
// Pinned rows sort to the top of the active list and show a pin glyph.
function isPinnedRow(c: ChatConversationRow): boolean {
  return (c.pinnedAt ?? 0) > 0;
}

export function ConversationListPanel(
  props: ConversationListPanelProps,
): JSX.Element {
  const {
    conversations,
    selectedConversationId,
    isLoading,
    error,
    onConversationPress,
    onConversationDelete,
    onConversationMuteToggle,
    onConversationArchiveToggle,
    onConversationPinToggle,
    onNewChatPress,
    onNewGroupPress,
    onCreateFolderPress,
    companies,
    onCompanyPress,
    onCreateCompany,
    filterTabs,
    onNotificationsPress,
    notificationsBadgeCount,
    hideResizeHandle,
    fillWidth,
    folders,
    conversationFolders,
    onToggleConversationFolder,
    onDeleteFolder,
  } = props;

  const tabs = filterTabs ?? DEFAULT_FILTER_TABS;
  const folderIdSet = useMemo(() => new Set((folders ?? []).map((f) => f.id)), [folders]);
  const [activeTab, setActiveTab] = useState<string>(tabs[0]?.id ?? "all");
  // If the active tab is a folder that no longer exists (just deleted), fall
  // back to "all" so the list doesn't filter to nothing.
  useEffect(() => {
    const isBuiltIn = activeTab === "all" || activeTab === "unread" || activeTab === "favourites" || activeTab === "dm";
    if (!isBuiltIn && !folderIdSet.has(activeTab)) {
      setActiveTab("all");
    }
  }, [activeTab, folderIdSet]);
  // Archive view toggle (the leading Archive tab). Transient per-mount state —
  // we intentionally DON'T persist it so a reload always lands on the live
  // list, never inside the archive. Only the user toggles it (no programmatic
  // close): an earlier "auto-exit when archivedCount === 0" effect raced the
  // Dexie liveQuery — opening the view before the freshly-archived row had
  // propagated into React state read `archivedCount === 0` and slammed it shut
  // a tick later ("opens for ~1s then closes"). An empty archive simply shows
  // the empty state; the user exits by tapping the tab again.
  const [archiveView, setArchiveView] = useState(false);
  const toggleArchiveView = useCallback((): void => {
    setArchiveView((v) => !v);
  }, []);

  // Two stable, independently-memoized lists. The active and archived panes
  // are BOTH kept mounted (see the render below) and merely toggled via
  // `display`, so switching between them never unmounts/remounts the rows —
  // avatars don't reload, each pane keeps its own scroll position, and there's
  // no list "refresh" flicker. The archived pane intentionally ignores the
  // All/Unread/Favourites/DM tab filter — archive is its own axis.
  const activeConversations = useMemo(() => {
    const filtered = conversations.filter((c) => {
      if (isArchivedRow(c)) return false;
      switch (activeTab) {
        case "all":
          return true;
        case "unread":
          return (c.unreadCount ?? 0) > 0;
        case "favourites":
          return c.favorite === true;
        case "dm":
          return c.kind === "direct";
        default:
          return conversationFolders?.[c.id]?.includes(activeTab) ?? false;
      }
    });
    // Pinned rows float to the top. `Array.sort` is stable, so within each
    // group the host's existing order (recency) is preserved.
    return filtered.sort(
      (a, b) => (isPinnedRow(b) ? 1 : 0) - (isPinnedRow(a) ? 1 : 0),
    );
  }, [conversations, activeTab, conversationFolders]);

  const archivedConversations = useMemo(
    () => conversations.filter(isArchivedRow),
    [conversations],
  );

  // Count of archived conversations — drives the Archive tab badge.
  const archivedCount = archivedConversations.length;

  // "Receiving N chats…" banner state. Driven entirely by the
  // `useChatSyncStore` — `useIncomingConversations` calls `noteJoin`
  // on every Welcome that produces a brand-new conversation row, and a
  // local setTimeout drains the set once the window elapses with no
  // new arrivals. The banner therefore appears during the
  // post-link reconciliation window and self-dismisses without any
  // explicit "done" signal from the linking flow.
  const recentJoinCount = useChatSyncStore((s) => s.recentJoinIds.size);
  const lastJoinAt = useChatSyncStore((s) => s.lastJoinAt);
  // Linking history-sync progress — the receiver (newly-linked) device shows a
  // true-% "Syncing conversations… N%" banner as the existing device's sealed
  // bundles replay. Falls back to an indeterminate "M messages" when the sender
  // is too old to declare a `batch_total`.
  const linkingSync = useChatSyncStore((s) => s.linkingSync);
  useEffect(() => {
    if (recentJoinCount === 0) return undefined;
    const t = setTimeout(() => {
      useChatSyncStore.getState().prune(lastJoinAt);
    }, RECENT_JOIN_WINDOW_MS);
    return () => clearTimeout(t);
  }, [recentJoinCount, lastJoinAt]);
  // Width comes from the shared zustand UI-preferences store so the
  // value persists via `zustand/middleware` (localStorage on web,
  // in-memory on desktop) without this component touching the storage
  // backend directly.
  const panelWidth = useUiPreferencesStore((s) => s.sidePanelWidth);
  const setPanelWidth = useUiPreferencesStore((s) => s.setSidePanelWidth);
  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(
    null,
  );
  // `isDragging` keeps the resize indicator visible while the user
  // drags even if the cursor leaves the 8-px hit zone. Without it,
  // flinging the mouse past the panel during a drag would flicker
  // the highlight off.
  const [isDragging, setIsDragging] = useState(false);

  // Web-only drag wiring. RN-macOS exposes a `window` global as a
  // compatibility shim BUT it has no `addEventListener` — so the
  // legacy `typeof window === "undefined"` guard returned false on
  // desktop and the next line crashed with
  // "window.addEventListener is not a function". Check method
  // presence explicitly so any non-DOM `window` (Hermes shim,
  // React Native, Node fastify) short-circuits cleanly.
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof (window as { addEventListener?: unknown }).addEventListener !== "function"
    ) {
      return undefined;
    }
    const handleMove = (e: MouseEvent): void => {
      const drag = dragStateRef.current;
      if (drag === null) return;
      const next = Math.min(
        Math.max(
          drag.startWidth + (e.clientX - drag.startX),
          SIDE_PANEL_MIN_WIDTH,
        ),
        SIDE_PANEL_MAX_WIDTH,
      );
      setPanelWidth(next);
    };
    const handleUp = (): void => {
      if (dragStateRef.current === null) return;
      dragStateRef.current = null;
      setIsDragging(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [setPanelWidth]);

  const beginDrag = (e: { clientX: number }): void => {
    dragStateRef.current = { startX: e.clientX, startWidth: panelWidth };
    setIsDragging(true);
    if (typeof document !== "undefined") {
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }
  };

  return (
    <View
      accessibilityLabel="Conversations"
      className={`relative flex flex-col h-full min-h-0 bg-light-surface-panel dark:bg-dark-surface-panel border-r border-light-border-subtle dark:border-dark-border-subtle ${
        fillWidth === true ? "w-full flex-1" : "shrink-0"
      }`}
      style={fillWidth === true ? undefined : { width: panelWidth }}
    >
      <PanelHeader
        onNotificationsPress={onNotificationsPress ?? (() => undefined)}
        notificationsBadgeCount={notificationsBadgeCount ?? 0}
        onNewChatPress={onNewChatPress}
        {...(onNewGroupPress !== undefined ? { onNewGroupPress } : {})}
        {...(onCreateFolderPress !== undefined ? { onCreateFolderPress } : {})}
      />

      <CompaniesSection
        companies={companies ?? []}
        onPress={onCompanyPress}
        onCreateCompany={onCreateCompany}
      />

      <ScrollView
        testID="chat-filters"
        accessibilityLabel="Conversation filter"
        horizontal
        showsHorizontalScrollIndicator={false}
        className="shrink-0 grow-0 ping-hide-scrollbar"
        contentContainerStyle={{
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 10,
          // Top + right padding so a folder chip's hover "×" badge (which
          // overhangs the chip corner) isn't clipped by the scroll viewport.
          paddingTop: 8,
          paddingBottom: 8,
          paddingRight: 8,
          columnGap: 8,
        }}
      >
        {(onConversationArchiveToggle !== undefined ||
          archivedCount > 0 ||
          archiveView) && (
          <>
            <ArchiveTab
              active={archiveView}
              count={archivedCount}
              onPress={toggleArchiveView}
            />
            <FilterDivider />
          </>
        )}
        {tabs.map((tab) => (
          <FilterChip
            key={tab.id}
            tab={tab}
            active={tab.id === activeTab}
            onPress={() => setActiveTab(tab.id)}
            {...(folderIdSet.has(tab.id) && onDeleteFolder !== undefined
              ? { onDelete: () => onDeleteFolder(tab.id) }
              : {})}
          />
        ))}
        <MoreFolderButton {...(onCreateFolderPress !== undefined ? { onPress: onCreateFolderPress } : {})} />
      </ScrollView>

      {linkingSync.active ? (
        <LinkingSyncBanner
          done={linkingSync.done}
          total={linkingSync.total}
          messages={linkingSync.messages}
        />
      ) : recentJoinCount > 0 ? (
        <SyncBanner count={recentJoinCount} />
      ) : null}

      {error !== null ? (
        <View className="flex-1 px-lg py-xl items-center">
          <Text className="font-sans text-footnote text-light-accent-rose dark:text-dark-accent-rose">
            Couldn't load conversations.
          </Text>
        </View>
      ) : isLoading ? (
        <ScrollView className="flex-1 min-h-0">
          <SkeletonRows />
        </ScrollView>
      ) : (
        // Active + Archived panes through the shared <TabView>: both scenes
        // stay mounted (no remount / re-fetch / scroll reset on switch) and
        // slide with an animated transition. The leading Archive tab and a
        // horizontal swipe both drive the index. No built-in tab bar — the
        // Archive tab above is the control.
        <TabView
          routes={CONVERSATION_TAB_ROUTES}
          index={archiveView ? 1 : 0}
          onIndexChange={(i) => setArchiveView(i === 1)}
          slideDurationMs={120}
          // Switch only via the Archive tab control — no drag-to-swap between
          // the active and archived lists.
          swipeEnabled={false}
          renderTabBar={() => null}
          renderScene={({ route }) =>
            route.key === "archived" ? (
              <ConversationListPane
                conversations={archivedConversations}
                archived
                selectedConversationId={selectedConversationId}
                onConversationPress={onConversationPress}
                onConversationDelete={onConversationDelete}
                onConversationMuteToggle={onConversationMuteToggle}
                onConversationArchiveToggle={onConversationArchiveToggle}
                folders={folders}
                conversationFolders={conversationFolders}
                onToggleConversationFolder={onToggleConversationFolder}
                empty={<ArchiveEmptyState />}
              />
            ) : (
              <ConversationListPane
                conversations={activeConversations}
                archived={false}
                selectedConversationId={selectedConversationId}
                onConversationPress={onConversationPress}
                onConversationDelete={onConversationDelete}
                onConversationMuteToggle={onConversationMuteToggle}
                onConversationArchiveToggle={onConversationArchiveToggle}
                onConversationPinToggle={onConversationPinToggle}
                folders={folders}
                conversationFolders={conversationFolders}
                onToggleConversationFolder={onToggleConversationFolder}
                empty={<EmptyState onNewChatPress={onNewChatPress} />}
              />
            )
          }
        />
      )}

      {hideResizeHandle !== true && (
        <ResizeHandle onBeginDrag={beginDrag} dragging={isDragging} />
      )}
    </View>
  );
}

// ── Resize handle (web-only interaction) ────────────────────────────

// ── Sync banner ─────────────────────────────────────────────────────
//
// Shows a transient "Receiving N chats…" row while the
// `useChatSyncStore` window is active (Welcomes are still flowing in
// from a post-link `admitDeviceToChats` round). Auto-dismisses
// `RECENT_JOIN_WINDOW_MS` after the last arrival via the prune timer
// in the parent.

interface SyncBannerProps {
  readonly count: number;
}

function SyncBanner({ count }: SyncBannerProps): JSX.Element {
  const label = count === 1 ? "Receiving 1 chat…" : `Receiving ${count} chats…`;
  return (
    <View
      accessibilityLabel={label}
      // RN's `AccessibilityRole` union doesn't include "status" (it's
      // a web-only ARIA role). The `accessibilityLabel` is enough for
      // screen readers; "alert" would announce too aggressively for a
      // transient progress signal, so we leave the role unset.
      accessibilityLiveRegion="polite"
      className="mx-sm mt-xs mb-[2px] p-xs rounded-sm bg-light-surface-elevated dark:bg-dark-surface-elevated border border-light-border-subtle dark:border-dark-border-subtle flex-row items-center"
      style={{ columnGap: 8 }}
    >
      <View
        className="h-2 w-2 rounded-full bg-light-accent-blue dark:bg-dark-accent-blue"
      />
      <Text className="font-sans text-footnote text-light-text-secondary dark:text-dark-text-secondary">
        {label}
      </Text>
    </View>
  );
}

// ── Linking history-sync banner ─────────────────────────────────────
//
// Shown on the RECEIVER (newly-linked) device while
// `consumePendingLinkingHistory` drains + replays the existing device's sealed
// history bundles. When the sender declared a `batch_total` (`total > 0`) we
// render a true percentage with a progress bar; otherwise we fall back to an
// indeterminate "M messages" count. Dismissed when the drain finishes
// (`linkingSync.active` flips false in the store).

interface LinkingSyncBannerProps {
  readonly done: number;
  readonly total: number;
  readonly messages: number;
}

function LinkingSyncBanner({ done, total, messages }: LinkingSyncBannerProps): JSX.Element {
  const determinate = total > 0;
  const pct = determinate ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const label = determinate
    ? `Syncing conversations… ${pct}%`
    : messages > 0
      ? `Syncing conversations… ${messages} ${messages === 1 ? 'message' : 'messages'}`
      : 'Syncing conversations…';
  return (
    <View
      accessibilityLabel={label}
      accessibilityLiveRegion="polite"
      className="mx-sm mt-xs mb-[2px] px-md py-xs rounded-md bg-light-surface-elevated dark:bg-dark-surface-elevated border border-light-border-subtle dark:border-dark-border-subtle"
    >
      <View className="flex-row items-center" style={{ columnGap: 8 }}>
        <View className="h-2 w-2 rounded-full bg-light-accent-blue dark:bg-dark-accent-blue" />
        <Text className="font-sans text-footnote text-light-text-secondary dark:text-dark-text-secondary">
          {label}
        </Text>
      </View>
      {determinate ? (
        <View className="mt-xs h-1 w-full rounded-full bg-light-border-subtle dark:bg-dark-border-subtle overflow-hidden">
          <View
            className="h-1 rounded-full bg-light-accent-blue dark:bg-dark-accent-blue"
            // Runtime-computed width — the one legitimate inline-style case.
            style={{ width: `${pct}%` }}
          />
        </View>
      ) : null}
    </View>
  );
}

interface ResizeHandleProps {
  readonly onBeginDrag: (e: { clientX: number }) => void;
  readonly dragging: boolean;
}

// 8 px hover/grab threshold. Wide enough to land the cursor on
// without micro-aiming, narrow enough that ordinary panel hovers
// don't trigger it. Centered on the panel's right border via
// `-mr-[4px]` so the visible strip sits exactly on the edge.
const HANDLE_HIT_WIDTH = 8;
// The visible strip the user sees once they're inside the hit zone
// (or while a drag is in progress). Narrower than the hit zone so
// the column-resize cursor activates before any colour change.
const HANDLE_VISIBLE_WIDTH = 2;

function ResizeHandle({
  onBeginDrag,
  dragging,
}: ResizeHandleProps): JSX.Element {
  const [hovered, setHovered] = useState(false);
  const showIndicator = hovered || dragging;

  // `View` on react-native-web forwards DOM event props. We type the
  // extras as `unknown` and cast so the macOS build (where these
  // props are no-ops) still typechecks.
  const domProps = {
    onMouseDown: (e: { clientX: number; preventDefault?: () => void }) => {
      e.preventDefault?.();
      onBeginDrag(e);
    },
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
  } as unknown as Record<string, unknown>;

  return (
    <View
      accessibilityRole="adjustable"
      accessibilityLabel="Resize conversations panel"
      className="absolute top-0 bottom-0 z-10"
      // `cursor: col-resize` is a CSS value RN-Web accepts but
      // React Native's `CursorValue` type doesn't include. The
      // cast keeps the desktop build typechecking while still
      // emitting the correct DOM style on web. NativeWind drops
      // `cursor-*` utilities, so inline-style is the documented
      // escape hatch per the project's Tailwind-first rule.
      // Positioning: the strip is centered on the panel's right
      // border via `right: -HALF`. The hit zone (`HANDLE_HIT_WIDTH`)
      // is what the mouse needs to enter for the cursor to flip;
      // anywhere inside the panel proper has no resize affordance.
      style={
        {
          cursor: "col-resize",
          width: HANDLE_HIT_WIDTH,
          right: -(HANDLE_HIT_WIDTH / 2),
        } as unknown as object
      }
      {...domProps}
    >
      <View
        accessibilityElementsHidden
        className={`h-full mx-auto bg-light-accent-brand dark:bg-dark-accent-brand ${
          showIndicator ? "opacity-70" : "opacity-0"
        }`}
        style={{ width: HANDLE_VISIBLE_WIDTH, pointerEvents: 'none' }}
      />
    </View>
  );
}

// ── Panel header ────────────────────────────────────────────────────

interface PanelHeaderProps {
  readonly onNotificationsPress: () => void;
  readonly notificationsBadgeCount?: number;
  readonly onNewChatPress: () => void;
  readonly onNewGroupPress?: () => void;
  readonly onCreateFolderPress?: () => void;
}

function PanelHeader({
  onNotificationsPress,
  notificationsBadgeCount = 0,
  onNewChatPress,
  onNewGroupPress,
  onCreateFolderPress,
}: PanelHeaderProps): JSX.Element {
  // The "+" opens the Figma Add menu (4036:14458) when a richer action set
  // is wired (group / folder); otherwise it falls back to firing New chat.
  const hasMenu = onNewGroupPress !== undefined || onCreateFolderPress !== undefined;
  const [menuOpen, setMenuOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const addBtnRef = useRef<any>(null);
  return (
    <View accessibilityRole="header" className="flex flex-col" style={{ zIndex: 50 }}>
      {/* Title row — Figma 2711-49552: "Chats" 20px/700 + bell + add icons, 48px tall */}
      <View
        className="relative z-50 flex flex-row items-center justify-between px-md h-12 bg-light-surface-panel dark:bg-dark-surface-panel"
        style={{ zIndex: 50 }}
      >
        <Text className="font-sans font-bold text-xl text-light-text-primary dark:text-[#F5F6F8]">
          Chats
        </Text>
        <View className="flex flex-row items-center" style={{ columnGap: 4 }}>
          <View className="relative">
            <IconChipButton
              icon={Bell}
              label="Notifications"
              testID="chat-notifications"
              onPress={onNotificationsPress}
            />
            {notificationsBadgeCount > 0 && (
              <View
                accessibilityLabel={`${notificationsBadgeCount} unread`}
                pointerEvents="none"
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full items-center justify-center bg-[#537EFF]"
              >
                <Text className="text-white text-[10px] font-bold leading-[14px]">
                  {notificationsBadgeCount > 99 ? '99+' : String(notificationsBadgeCount)}
                </Text>
              </View>
            )}
          </View>
          <View ref={addBtnRef as never}>
            <IconChipButton
              icon={Plus}
              label={hasMenu ? "Create" : "New chat"}
              testID="chat-new-chat"
              onPress={() => {
                if (hasMenu) setMenuOpen((v) => !v);
                else onNewChatPress();
              }}
            />
          </View>
        </View>

        {/* Add menu popover (Figma 4036:14458) */}
        {hasMenu ? (
          <AnchoredPopover
            anchorRef={addBtnRef}
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            placement="bottom"
            align="end"
          >
            <View className="w-[253px] rounded-[12px] p-1 bg-light-surface-primary dark:bg-dark-surface-panel border border-light-border-secondary dark:border-white/[0.06] shadow-2xl">
              <AddMenuRow
                icon="chat"
                label="New message"
                onPress={() => {
                  setMenuOpen(false);
                  onNewChatPress();
                }}
              />
              {onNewGroupPress !== undefined ? (
                <AddMenuRow
                  icon="people"
                  label="New group"
                  onPress={() => {
                    setMenuOpen(false);
                    onNewGroupPress();
                  }}
                />
              ) : null}
              {onCreateFolderPress !== undefined ? (
                <>
                  <View className="h-px my-1 bg-light-border-secondary dark:bg-white/[0.06]" />
                  <AddMenuRow
                    icon="folder"
                    label="Create new folder"
                    chevron
                    onPress={() => {
                      setMenuOpen(false);
                      onCreateFolderPress();
                    }}
                  />
                </>
              ) : null}
            </View>
          </AnchoredPopover>
        ) : null}
      </View>

    </View>
  );
}

// A single Add-menu row — leading Figma glyph + label, optional trailing
// chevron (Figma 4036:14458 `option-item`, r8, 14px label).
function AddMenuRow({
  icon,
  label,
  chevron,
  onPress,
}: {
  icon: 'chat' | 'people' | 'folder';
  label: string;
  chevron?: boolean;
  onPress: () => void;
}): JSX.Element {
  const scheme = useColorScheme();
  const iconColor =
    tokens.color.semantic[scheme === "dark" ? "dark" : "light"].text.primary;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      className="flex flex-row items-center gap-sm px-sm h-7 rounded-[8px] hover:bg-light-surface-tertiary dark:hover:bg-white/[0.06]"
    >
      <NewChatIcon name={icon} size={16} color={iconColor} />
      <Text className="flex-1 font-sans text-[14px] leading-5 text-light-text-primary dark:text-[#E8EAED]">
        {label}
      </Text>
      {chevron === true ? (
        <ChevronRight size={16} strokeWidth={2} color={iconColor} />
      ) : null}
    </Pressable>
  );
}

interface IconChipButtonProps {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly onPress: () => void;
  // Stable anchor for product tours / e2e. Maps to `data-testid` on web.
  readonly testID?: string;
}

function IconChipButton({
  icon: Icon,
  label,
  onPress,
  testID,
}: IconChipButtonProps): JSX.Element {
  const scheme = useColorScheme();
  const color =
    tokens.color.semantic[scheme === "dark" ? "dark" : "light"].icon.muted;
  return (
    <Pressable
      {...(testID !== undefined ? { testID } : {})}
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      className="flex items-center justify-center w-7 h-7 rounded-[6px] hover:bg-light-surface-elevated dark:hover:bg-dark-surface-elevated"
    >
      <Icon size={18} strokeWidth={1.75} color={color} />
    </Pressable>
  );
}

// ── Companies section (hidden when prop is empty) ───────────────────

interface CompaniesSectionProps {
  readonly companies: readonly CompanyRow[];
  readonly onPress?: ((id: string) => void) | undefined;
  readonly onCreateCompany?: (() => void) | undefined;
}

function CompaniesSection({
  companies,
  onPress,
  onCreateCompany,
}: CompaniesSectionProps): JSX.Element {
  const scheme = useColorScheme();
  const iconColor =
    tokens.color.semantic[scheme === "dark" ? "dark" : "light"].icon.muted;

  if (companies.length === 0) {
    // Empty state — Figma 1668-55872: "Companies" label + "Create a company" row.
    return (
      <View className="flex flex-col px-md pb-sm gap-xs">
        <View className="flex flex-row items-center justify-between">
          <Text className="font-normal text-[12px] leading-[17px] text-light-text-secondary dark:text-[#6B7280]">
            Your Companies
          </Text>
        </View>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Create a company"
          onPress={onCreateCompany}
          className="flex flex-row items-center gap-sm h-[25px] rounded-[8px] px-xs"
        >
          <Plus size={14} strokeWidth={1.75} color={iconColor} />
          <Text className="font-sans font-normal text-[12px] leading-[17px] text-light-text-primary dark:text-[#FFFFFF]">
            Create a company
          </Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View
      accessibilityLabel="Your companies"
      className="flex flex-col px-md pb-sm gap-xs"
    >
      <Text className="font-normal mb-1 text-[12px] leading-[17px] text-light-text-secondary dark:text-[#6B7280]">
        Your Compan{companies?.length > 1 ? 'ies' : 'y'}
      </Text>
      <View className="flex flex-col gap-xs">
        {companies.map((company) => (
          <Pressable
            key={company.id}
            accessibilityRole="button"
            accessibilityLabel={`Open ${company.name}`}
            onPress={() => onPress?.(company.id)}
            className="flex flex-row items-center gap-sm px-xs"
          >
            <Avatar
              size="company"
              shape="rounded"
              initials={company.name.charAt(0).toUpperCase() || "?"}
              {...(company.avatarUri !== undefined
                ? { imageUri: company.avatarUri }
                : {})}
            />
            <Text
              numberOfLines={1}
              className="flex-1 font-normal text-sm leading-[22px] text-light-text-primary dark:text-dark-text-primary"
            >
              {company.name}
            </Text>
          </Pressable>
        ))}
      </View>
    </View>
  );
}

// ── Filter chips (All / Main Work / Side Projects + more) ───────────

interface FilterChipProps {
  readonly tab: ConversationFilterTab;
  readonly active: boolean;
  readonly onPress: () => void;
  /** When set, the chip is a deletable folder: a "×" appears on hover and
   *  tapping it removes the folder. */
  readonly onDelete?: () => void;
}

function FilterChip({ tab, active, onPress, onDelete }: FilterChipProps): JSX.Element {
  const [isHovered, setIsHovered] = useState(false);
  const containerClass = active
    ? "bg-light-surface-elevated dark:bg-dark-surface-elevated border border-light-border-strong dark:border-dark-border-strong"
    : "bg-light-surface-panel dark:bg-dark-surface-panel border border-light-border-subtle dark:border-dark-border-subtle";
  const textClass = active
    ? "text-light-text-primary dark:text-dark-text-primary"
    : "text-light-text-secondary dark:text-dark-text-secondary";
  // DOM-level hover (RN-Web drops unknown props before the <div>); native no-ops.
  const hoverHandlers = {
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    onPointerEnter: () => setIsHovered(true),
    onPointerLeave: () => setIsHovered(false),
  } as Record<string, unknown>;
  return (
    <View className="relative" {...hoverHandlers}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ selected: active }}
        onPress={onPress}
        className={`flex flex-row items-center gap-xs h-8 px-md rounded-[10px] ${containerClass}`}
      >
        <Text
          className={`font-sans font-normal text-[14px] leading-[17px] ${textClass}`}
        >
          {tab.label}
        </Text>
      </Pressable>
      {onDelete !== undefined && isHovered ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Delete ${tab.label} folder`}
          onPress={(event) => {
            if (
              event &&
              typeof (event as { stopPropagation?: () => void }).stopPropagation === "function"
            ) {
              (event as unknown as { stopPropagation: () => void }).stopPropagation();
            }
            onDelete();
          }}
          className="absolute -right-1.5 -top-1.5 z-10 w-4 h-4 items-center justify-center rounded-full bg-light-accent-rose dark:bg-dark-accent-rose"
        >
          <X size={10} strokeWidth={2.5} color="#FFFFFF" />
        </Pressable>
      ) : null}
    </View>
  );
}

// Folder picker popover — rendered through the web body portal so it escapes
// the conversation list's overflow + stacking context (z-index). Anchored above
// the folder button (flips below when there's no room).
function FolderPickerPopover({
  anchorRef,
  open,
  folders,
  folderIds,
  glyphColor,
  onToggle,
  onDismiss,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly anchorRef: React.MutableRefObject<any>;
  readonly open: boolean;
  readonly folders: readonly { readonly id: string; readonly name: string }[];
  readonly folderIds: readonly string[];
  readonly glyphColor: string;
  readonly onToggle: (folderId: string) => void;
  readonly onDismiss: () => void;
}): JSX.Element {
  // AnchoredPopover owns positioning (flip/clamp/portal) + outside-click/Escape.
  return (
    <AnchoredPopover anchorRef={anchorRef} open={open} onClose={onDismiss} placement="top" align="end">
      <View className="w-[220px] rounded-[12px] p-1 bg-light-surface-primary dark:bg-[#15171C] border border-light-border-secondary dark:border-white/[0.06] shadow-2xl">
        {folders.map((f) => {
          const inFolder = folderIds.includes(f.id);
          return (
            <Pressable
              key={f.id}
              accessibilityRole="button"
              accessibilityState={{ selected: inFolder }}
              onPress={() => onToggle(f.id)}
              className="flex-row items-center gap-2 h-9 px-2 rounded-[8px] active:opacity-80 hover:bg-light-surface-tertiary dark:hover:bg-white/[0.04]"
            >
              <Folder size={16} strokeWidth={1.75} color={glyphColor} />
              <Text
                numberOfLines={1}
                className="flex-1 text-[14px] text-light-text-primary dark:text-dark-text-primary"
              >
                {f.name}
              </Text>
              {inFolder ? <Check size={16} strokeWidth={2} color="#537EFF" /> : null}
            </Pressable>
          );
        })}
      </View>
    </AnchoredPopover>
  );
}

// Leading Archive tab — 32x32 rounded-rect, sits before the text tabs.
// Toggles the conversation list between the live chats and the archived ones.
// Archive glyph mirrors the Figma design-system archive icon (box + handle).
function ArchiveTab({
  active,
  count,
  onPress,
}: {
  active: boolean;
  count: number;
  onPress: () => void;
}): JSX.Element {
  const scheme = useColorScheme();
  const c = tokens.color.semantic[scheme === "dark" ? "dark" : "light"];
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={active ? "Show active chats" : "Show archived chats"}
      accessibilityState={{ selected: active }}
      onPress={onPress}
      className={`relative flex flex-row items-center justify-center w-8 h-8 rounded-[10px] border ${
        active
          ? "bg-light-surface-elevated dark:bg-dark-surface-elevated border-light-border-strong dark:border-dark-border-strong"
          : "bg-light-surface-panel dark:bg-dark-surface-panel border-light-border-subtle dark:border-dark-border-subtle"
      }`}
    >
      <ArchiveGlyph size={16} color={active ? c.icon.default : c.icon.muted} />
      {/* Unread-style count badge so the user knows chats are tucked away even
          when the tab is inactive. Hidden at zero. */}
      {!active && count > 0 ? (
        <View className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full items-center justify-center bg-light-accent-brand dark:bg-[#537EFF]">
          <Text className="font-sans text-[10px] leading-[12px] font-bold text-white">
            {count > 99 ? "99+" : count}
          </Text>
        </View>
      ) : null}
    </Pressable>
  );
}

// 1px vertical divider separating the leading tab from the text tabs.
function FilterDivider(): JSX.Element {
  return (
    <View className="self-stretch w-px my-1 bg-light-border-subtle dark:bg-dark-border-subtle" />
  );
}

function MoreFolderButton({ onPress }: { onPress?: () => void }): JSX.Element {
  const scheme = useColorScheme();
  const c = tokens.color.semantic[scheme === "dark" ? "dark" : "light"];
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="Create folder"
      onPress={onPress}
      className="flex flex-row items-center justify-center w-8 h-8 rounded-full bg-light-surface-panel dark:bg-dark-surface-panel border border-light-border-subtle dark:border-dark-border-subtle active:opacity-80"
    >
      <Folder size={14} strokeWidth={1.75} color={c.icon.muted} />
    </Pressable>
  );
}

// ── Conversation row ────────────────────────────────────────────────

interface ConversationRowProps {
  readonly conversation: ChatConversationRow;
  readonly selected: boolean;
  readonly onPress: () => void;
  /** Optional delete handler. When supplied, a trash icon appears on
   *  hover (RN-Web + RN-macOS) and tapping it calls back so the host
   *  can purge the conversation locally + on the server. */
  readonly onDelete?: () => void;
  /** Optional mute toggle. When supplied, a bell toggle appears on hover;
   *  `conversation.muted` drives the icon + the persistent glyph. */
  readonly onMuteToggle?: () => void;
  /** Whether this row is being shown in the Archive view (drives the
   *  archive-vs-unarchive hover icon + label). */
  readonly archived?: boolean;
  /** Optional archive toggle. When supplied, an archive (or, in the Archive
   *  view, an unarchive) button appears on hover. */
  readonly onArchiveToggle?: () => void;
  /** Whether this conversation is pinned (drives the pin glyph + hover icon). */
  readonly pinned?: boolean;
  /** Optional pin toggle. When supplied, a pin / unpin button appears on hover. */
  readonly onPinToggle?: () => void;
  /** Folders available for assignment — when supplied, a folder button on hover
   *  opens a picker to toggle this conversation's membership. */
  readonly folders?: readonly { readonly id: string; readonly name: string }[];
  /** This conversation's current folder ids (drives the picker's checkmarks). */
  readonly folderIds?: readonly string[];
  readonly onToggleFolder?: (folderId: string) => void;
}

// Memoized so a re-render of `ConversationListPanel` (triggered by
// any single conversation's mutation — Dexie `liveQuery` fires the
// whole list query on every message arrival) doesn't re-render EVERY
// row. With ~50 chats open + a steady inbound message stream that's
// 50x reduction in the chat-list render cost per peer message.
// Cost basis: shallow prop equality (conversation, selected, onPress).
// Caller MUST pass a stable `onPress` (useCallback'd outside the map).
//
// `ChatConversationRow` is the parent-owned object; we rely on the
// adapter producing referentially-stable rows for unchanged
// conversations. `conversationsAdapter.web.ts` materialises a new
// row only when one of its fields actually changes, so React.memo's
// shallow comparison works as expected.
/**
 * Custom equality for `ConversationRow`. The Dexie `liveQuery` re-emits the
 * WHOLE conversation list (with freshly-materialised row objects + fresh inline
 * handler closures from the pane's `.map`) on EVERY inbound message, so the
 * default shallow `memo` compares unequal for every row and re-renders all of
 * them per message. This comparator instead compares the exact fields the row
 * RENDERS by value (so a new-but-identical row is treated as equal) and
 * deliberately ignores the handler identities — they are behaviourally stable
 * (each just calls the parent callback with this row's id). Net: a row
 * re-renders only when its own rendered content actually changes.
 */
function conversationRowPropsEqual(
  prev: ConversationRowProps,
  next: ConversationRowProps,
): boolean {
  if (
    prev.selected !== next.selected ||
    prev.archived !== next.archived ||
    prev.pinned !== next.pinned
  ) {
    return false;
  }
  const a = prev.conversation;
  const b = next.conversation;
  if (a !== b) {
    if (
      a.id !== b.id ||
      a.name !== b.name ||
      a.avatarUri !== b.avatarUri ||
      a.verified !== b.verified ||
      a.muted !== b.muted ||
      a.unreadCount !== b.unreadCount ||
      a.joinedViaInvite !== b.joinedViaInvite ||
      a.activeCall?.media !== b.activeCall?.media ||
      a.lastMessage?.preview !== b.lastMessage?.preview ||
      a.lastMessage?.timestamp !== b.lastMessage?.timestamp
    ) {
      return false;
    }
  }
  // Folder chips: compare presence + this row's folder membership by value.
  if ((prev.folders?.length ?? 0) !== (next.folders?.length ?? 0)) return false;
  const pf = prev.folderIds ?? [];
  const nf = next.folderIds ?? [];
  if (pf.length !== nf.length) return false;
  for (let i = 0; i < pf.length; i += 1) {
    if (pf[i] !== nf[i]) return false;
  }
  return true;
}

const ConversationRow = memo(ConversationRowInner, conversationRowPropsEqual);

// Windowed-list helpers (fixed 50px row + 2px gap → exact `getItemLayout`).
const CONVERSATION_ROW_HEIGHT = 50;
const CONVERSATION_ROW_GAP = 2;
const keyExtractor = (c: ChatConversationRow): string => c.id;
function ConversationRowSeparator(): JSX.Element {
  return <View className="h-[2px]" />;
}
function getConversationRowLayout(
  _data: ArrayLike<ChatConversationRow> | null | undefined,
  index: number,
): { length: number; offset: number; index: number } {
  return {
    length: CONVERSATION_ROW_HEIGHT,
    offset: (CONVERSATION_ROW_HEIGHT + CONVERSATION_ROW_GAP) * index,
    index,
  };
}

// One scrollable list pane (active OR archived). Both panes are rendered at
// once as <TabView> scenes and kept mounted, so switching tabs never unmounts
// a pane — rows keep their mounted state (no avatar reload) and each pane
// retains its own scroll position. `empty` renders when the pane has no rows.
interface ConversationListPaneProps {
  readonly conversations: readonly ChatConversationRow[];
  readonly archived: boolean;
  readonly selectedConversationId: string | null;
  readonly onConversationPress: (id: string) => void;
  readonly onConversationDelete?: ((id: string) => void) | undefined;
  readonly onConversationMuteToggle?: ((id: string, muted: boolean) => void) | undefined;
  readonly onConversationArchiveToggle?: ((id: string, archived: boolean) => void) | undefined;
  readonly onConversationPinToggle?: ((id: string, pinned: boolean) => void) | undefined;
  readonly folders?: readonly { readonly id: string; readonly name: string }[] | undefined;
  readonly conversationFolders?: Readonly<Record<string, readonly string[]>> | undefined;
  readonly onToggleConversationFolder?: ((conversationId: string, folderId: string) => void) | undefined;
  readonly empty: JSX.Element;
}

function ConversationListPane({
  conversations,
  archived,
  selectedConversationId,
  onConversationPress,
  onConversationDelete,
  onConversationMuteToggle,
  onConversationArchiveToggle,
  onConversationPinToggle,
  folders,
  conversationFolders,
  onToggleConversationFolder,
  empty,
}: ConversationListPaneProps): JSX.Element {
  // Row renderer for the windowed list. The per-row closures are recreated per
  // render, but `ConversationRow`'s custom comparator ignores handler identity,
  // so this does not defeat the memo — only genuinely-changed rows re-render.
  const renderItem = useCallback<ListRenderItem<ChatConversationRow>>(
    ({ item: c }) => (
      <ConversationRow
        conversation={c}
        selected={c.id === selectedConversationId}
        onPress={() => onConversationPress(c.id)}
        {...(onConversationDelete !== undefined
          ? { onDelete: () => onConversationDelete(c.id) }
          : {})}
        {...(onConversationMuteToggle !== undefined
          ? { onMuteToggle: () => onConversationMuteToggle(c.id, !(c.muted ?? false)) }
          : {})}
        {...(onConversationArchiveToggle !== undefined
          ? { archived, onArchiveToggle: () => onConversationArchiveToggle(c.id, !archived) }
          : {})}
        {...(onConversationPinToggle !== undefined
          ? { pinned: isPinnedRow(c), onPinToggle: () => onConversationPinToggle(c.id, !isPinnedRow(c)) }
          : {})}
        {...(folders !== undefined && folders.length > 0 && onToggleConversationFolder !== undefined
          ? {
              folders,
              folderIds: conversationFolders?.[c.id] ?? [],
              onToggleFolder: (folderId: string) => onToggleConversationFolder(c.id, folderId),
            }
          : {})}
      />
    ),
    [
      selectedConversationId,
      onConversationPress,
      onConversationDelete,
      onConversationMuteToggle,
      archived,
      onConversationArchiveToggle,
      onConversationPinToggle,
      folders,
      conversationFolders,
      onToggleConversationFolder,
    ],
  );

  if (conversations.length === 0) {
    return <ScrollView className="flex-1 min-h-0">{empty}</ScrollView>;
  }

  // Windowed list (RN FlatList works on react-native-web + macos) — only the
  // visible rows mount, holding the 1000-conversation / 60fps budget. Rows are a
  // fixed 50px with a 2px gap, so `getItemLayout` is exact (no measurement).
  return (
    <FlatList
      className="flex-1 min-h-0"
      contentContainerClassName="py-xs px-sm"
      data={conversations}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      ItemSeparatorComponent={ConversationRowSeparator}
      getItemLayout={getConversationRowLayout}
      windowSize={10}
      initialNumToRender={20}
      maxToRenderPerBatch={20}
    />
  );
}

function ConversationRowInner({
  conversation,
  selected,
  onPress,
  onDelete,
  onMuteToggle,
  archived,
  onArchiveToggle,
  pinned,
  onPinToggle,
  folders,
  folderIds,
  onToggleFolder,
}: ConversationRowProps): JSX.Element {
  const isMuted = conversation.muted === true;
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);
  // AnchoredPopover measures the folder button (via this ref) itself, so no
  // manual rect capture is needed.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const folderBtnRef = useRef<any>(null);
  const canPickFolder = folders !== undefined && folders.length > 0 && onToggleFolder !== undefined;
  const openFolderPicker = useCallback((): void => {
    setFolderPickerOpen((o) => !o);
  }, []);
  const title = conversation.name ?? "Unnamed conversation";
  const preview = conversation.lastMessage?.preview ?? "No messages yet";
  // Unsent draft for this conversation (localStorage, keyed by conversation id —
  // the same key the composer writes). Shown as a red "Draft:" prefix taking
  // precedence over the last-message preview (iOS parity).
  const draftText = peekComposerDraft(conversation.id);
  const time =
    conversation.lastMessage?.timestamp !== undefined
      ? formatTimestamp(conversation.lastMessage.timestamp)
      : "";
  const unread = conversation.unreadCount ?? 0;
  const hasUnread = unread > 0;
  const isNew =
    conversation.joinedViaInvite === true &&
    conversation.lastMessage === undefined;
  const [isHovered, setIsHovered] = useState(false);
  // DOM-level pointer hover events. `Pressable` on RN-Web drops
  // unknown props before they reach the underlying `<div>`, so the
  // hover state has to live on the wrapping container's native
  // events. Native touch platforms simply never fire these.
  const rowHoverHandlers = {
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    onPointerEnter: () => setIsHovered(true),
    onPointerLeave: () => setIsHovered(false),
  } as Record<string, unknown>;
  const scheme = useColorScheme();
  const trashGlyphColor =
    tokens.color.semantic[scheme === "dark" ? "dark" : "light"].accent.rose;
  const mutedGlyphColor =
    tokens.color.semantic[scheme === "dark" ? "dark" : "light"].text.secondary;

  // Brighter preview + bold timestamp when unread (matches Figma row 0).
  const previewClass = hasUnread
    ? "text-light-text-primary dark:text-dark-text-primary"
    : "text-light-text-secondary dark:text-dark-text-secondary";
  const timeClass = hasUnread
    ? "font-bold text-light-text-primary dark:text-dark-text-primary"
    : "font-normal text-light-text-secondary dark:text-dark-text-secondary";

  return (
    // Wrapper owns the hover state + is the positioning context for the
    // absolutely-pinned hover actions. The actions are SIBLINGS of the row
    // Pressable (not children) so we never nest a <button> inside a <button>
    // (RN-Web renders accessibilityRole="button" as a real <button>).
    <View className="relative" {...rowHoverHandlers}>
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected }}
      onPress={onPress}
      className={`relative flex flex-row items-center gap-md h-[50px] rounded-[8px] ${
        selected
          ? "bg-light-surface-elevated dark:bg-dark-surface-elevated"
          : "hover:bg-light-surface-elevated/40 dark:hover:bg-dark-surface-elevated/40"
      }`}
      style={{ paddingHorizontal: 8, paddingVertical: 4 }}
    >
      <Avatar
        size="small"
        shape="rounded"
        initials={title.charAt(0).toUpperCase() || "?"}
        {...(conversation.avatarUri !== undefined
          ? { imageUri: conversation.avatarUri }
          : {})}
      />
      <View className="flex-1 min-w-0 flex flex-col gap-[2px]">
        <View className="flex flex-row items-center justify-between gap-sm">
          <View className="flex flex-row items-center gap-xs flex-1 min-w-0">
            <Text
              numberOfLines={1}
              className="font-sans font-bold text-[16px] leading-[22px] text-light-text-primary dark:text-dark-text-primary"
            >
              {title}
            </Text>
            <VerifiedBadge
              verified={conversation.verified === true}
              size={16}
            />
            {pinned === true && (
              <View accessibilityRole="image" accessibilityLabel="Pinned">
                <Pin size={13} strokeWidth={1.75} color={mutedGlyphColor} fill={mutedGlyphColor} />
              </View>
            )}
            {isMuted && (
              <View accessibilityRole="image" accessibilityLabel="Muted">
                <BellOff size={13} strokeWidth={1.75} color={mutedGlyphColor} />
              </View>
            )}
            {isNew && (
              <View accessibilityRole="image" accessibilityLabel="New chat">
                <NewIndicator />
              </View>
            )}
            {conversation.activeCall !== undefined && (
              <View accessibilityRole="image" accessibilityLabel={`Active ${conversation.activeCall.media} call`}>
                <ActiveCallBadge media={conversation.activeCall.media} />
              </View>
            )}
          </View>
          {time !== "" && (
            <Text
              className={`font-sans text-[12px] leading-[17px] shrink-0 ${timeClass}`}
            >
              {time}
            </Text>
          )}
        </View>
        <View className="flex flex-row items-center justify-between gap-sm">
          {draftText.length > 0 ? (
            <Text numberOfLines={1} className={`flex-1 ${previewClass}`}>
              <Text className="text-[#E5484D]">Draft: </Text>
              {draftText}
            </Text>
          ) : (
            <MessagePreview
              preview={preview}
              unread={hasUnread}
              className={previewClass}
            />
          )}
          {/* Unread-count badge — sits directly UNDER the timestamp
              (preview-row right edge), primary-tint rounded pill. Only
              when there are unread messages AND this isn't the open chat
              (the user is "not in chat"). Hidden while the delete control
              is showing on hover so they don't overlap. */}
          {hasUnread && !selected && !isHovered ? (
            <View className="shrink-0 min-w-[18px] h-[18px] px-1.5 rounded-full items-center justify-center bg-light-accent-brand dark:bg-[#537EFF]">
              <Text className="font-sans text-[11px] leading-[14px] font-bold text-white">
                {unread > 99 ? "99+" : unread}
              </Text>
            </View>
          ) : null}
        </View>
      </View>
    </Pressable>
      {/* Hover actions — a single absolutely-pinned row at the bottom-right,
          surfaced on hover (pointer devices). Siblings of the row Pressable so
          they are real <button>s without nesting inside the row's <button>. */}
      {isHovered || folderPickerOpen ? (
        <View className="absolute bottom-1 right-1 flex-row items-center gap-sm">
          {onPinToggle !== undefined ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={pinned === true ? "Unpin conversation" : "Pin conversation"}
              onPress={(event) => {
                if (
                  event &&
                  typeof (event as { stopPropagation?: () => void }).stopPropagation === "function"
                ) {
                  (event as unknown as { stopPropagation: () => void }).stopPropagation();
                }
                onPinToggle();
              }}
              className="w-[28px] h-[28px] rounded-[8px] items-center justify-center bg-light-surface-secondary dark:bg-dark-surface-secondary hover:bg-light-surface-tertiary dark:hover:bg-dark-surface-tertiary"
            >
              {pinned === true ? (
                <PinOff size={16} strokeWidth={1.75} color={mutedGlyphColor} />
              ) : (
                <Pin size={16} strokeWidth={1.75} color={mutedGlyphColor} />
              )}
            </Pressable>
          ) : null}
          {canPickFolder ? (
            <Pressable
              ref={folderBtnRef as never}
              accessibilityRole="button"
              accessibilityLabel="Add to folder"
              onPress={(event) => {
                if (
                  event &&
                  typeof (event as { stopPropagation?: () => void }).stopPropagation === "function"
                ) {
                  (event as unknown as { stopPropagation: () => void }).stopPropagation();
                }
                openFolderPicker();
              }}
              className="w-[28px] h-[28px] rounded-[8px] items-center justify-center bg-light-surface-secondary dark:bg-dark-surface-secondary hover:bg-light-surface-tertiary dark:hover:bg-dark-surface-tertiary"
            >
              <FolderPlus size={16} strokeWidth={1.75} color={mutedGlyphColor} />
            </Pressable>
          ) : null}
          {onMuteToggle !== undefined ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={isMuted ? "Unmute conversation" : "Mute conversation"}
              onPress={(event) => {
                if (
                  event &&
                  typeof (event as { stopPropagation?: () => void }).stopPropagation === "function"
                ) {
                  (event as unknown as { stopPropagation: () => void }).stopPropagation();
                }
                onMuteToggle();
              }}
              className="w-[28px] h-[28px] rounded-[8px] items-center justify-center bg-light-surface-secondary dark:bg-dark-surface-secondary hover:bg-light-surface-tertiary dark:hover:bg-dark-surface-tertiary"
            >
              {isMuted ? (
                <Bell size={16} strokeWidth={1.75} color={mutedGlyphColor} />
              ) : (
                <BellOff size={16} strokeWidth={1.75} color={mutedGlyphColor} />
              )}
            </Pressable>
          ) : null}
          {onArchiveToggle !== undefined ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={archived === true ? "Unarchive conversation" : "Archive conversation"}
              onPress={(event) => {
                if (
                  event &&
                  typeof (event as { stopPropagation?: () => void }).stopPropagation === "function"
                ) {
                  (event as unknown as { stopPropagation: () => void }).stopPropagation();
                }
                onArchiveToggle();
              }}
              className="w-[28px] h-[28px] rounded-[8px] items-center justify-center bg-light-surface-secondary dark:bg-dark-surface-secondary hover:bg-light-surface-tertiary dark:hover:bg-dark-surface-tertiary"
            >
              {archived === true ? (
                <ArchiveRestore size={16} strokeWidth={1.75} color={mutedGlyphColor} />
              ) : (
                <ArchiveGlyph size={16} color={mutedGlyphColor} />
              )}
            </Pressable>
          ) : null}
          {onDelete !== undefined ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Delete conversation"
              onPress={(event) => {
                if (
                  event &&
                  typeof (event as { stopPropagation?: () => void }).stopPropagation === "function"
                ) {
                  (event as unknown as { stopPropagation: () => void }).stopPropagation();
                }
                onDelete();
              }}
              className="w-[28px] h-[28px] rounded-[8px] items-center justify-center bg-light-surface-secondary dark:bg-dark-surface-secondary hover:bg-light-surface-tertiary dark:hover:bg-dark-surface-tertiary"
            >
              <Trash2 size={16} strokeWidth={1.75} color={trashGlyphColor} />
            </Pressable>
          ) : null}
        </View>
      ) : null}
      {/* Folder picker — AnchoredPopover anchors above the folder button,
          flips/clamps to the viewport, and dismisses on outside-click/Escape. */}
      {canPickFolder ? (
        <FolderPickerPopover
          anchorRef={folderBtnRef}
          open={folderPickerOpen}
          folders={folders ?? []}
          folderIds={folderIds ?? []}
          glyphColor={mutedGlyphColor}
          onToggle={(folderId) => onToggleFolder?.(folderId)}
          onDismiss={() => setFolderPickerOpen(false)}
        />
      ) : null}
    </View>
  );
}

function NewIndicator(): JSX.Element {
  const scheme = useColorScheme();
  const color =
    tokens.color.semantic[scheme === "dark" ? "dark" : "light"].accent.brand;
  return <MessageCirclePlus size={14} color={color} />;
}

// Green phone/video icon shown on list rows and grid cells when a call is
// currently active in that conversation.
function ActiveCallBadge({ media, size = 13 }: { readonly media: 'audio' | 'video'; readonly size?: number }): JSX.Element {
  return media === 'video'
    ? <Video size={size} strokeWidth={2} color="#3CCB4F" />
    : <PhoneCall size={size} strokeWidth={2} color="#3CCB4F" />;
}

// ── Skeleton + empty placeholders ───────────────────────────────────

function SkeletonRows(): JSX.Element {
  return (
    <View className="flex flex-col py-xs px-sm gap-xs">
      {Array.from({ length: 8 }).map((_, i) => (
        <View
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton has no semantic key
          key={i}
          className="flex flex-row items-center gap-md p-md"
        >
          <View className="w-10 h-10 rounded-[12px] bg-light-surface-elevated dark:bg-dark-surface-elevated" />
          <View className="flex-1 flex flex-col gap-xs">
            <View className="h-3 w-2/3 rounded bg-light-surface-elevated dark:bg-dark-surface-elevated" />
            <View className="h-3 w-5/6 rounded bg-light-surface-elevated dark:bg-dark-surface-elevated" />
          </View>
        </View>
      ))}
    </View>
  );
}

// Empty placeholder for the Archive view (no archived chats). Mirrors the
// `EmptyState` chrome minus the "start a chat" CTA — archiving is the action
// that fills this list, so there's nothing to prompt here.
function ArchiveEmptyState(): JSX.Element {
  const scheme = useColorScheme();
  const archiveGlyphColor =
    tokens.color.semantic[scheme === "dark" ? "dark" : "light"].icon.muted;
  return (
    <View className="flex flex-col items-center px-lg py-xxl gap-md">
      <View
        accessibilityElementsHidden
        className="flex items-center justify-center w-16 h-16 rounded-full bg-light-surface-elevated dark:bg-dark-surface-elevated"
      >
        <ArchiveGlyph size={24} color={archiveGlyphColor} />
      </View>
      <View className="flex flex-col gap-xs items-center">
        <Text className="font-sans text-body font-semibold text-light-text-primary dark:text-dark-text-primary text-center">
          No archived chats
        </Text>
        <Text className="font-sans text-footnote text-light-text-secondary dark:text-dark-text-secondary max-w-[260px] text-center">
          Archived conversations stay here, hidden from your main list and
          synced across your devices.
        </Text>
      </View>
    </View>
  );
}

interface EmptyStateProps {
  readonly onNewChatPress: () => void;
}

function EmptyState({ onNewChatPress }: EmptyStateProps): JSX.Element {
  return (
    <View className="flex flex-col items-center px-lg py-xxl gap-md">
      <View
        accessibilityElementsHidden
        className="flex items-center justify-center w-16 h-16 rounded-full bg-light-surface-elevated dark:bg-dark-surface-elevated"
      >
        <Users />
      </View>
      <View className="flex flex-col gap-xs items-center">
        <Text className="font-sans text-body font-semibold text-light-text-primary dark:text-dark-text-primary text-center">
          No chats yet
        </Text>
        <Text className="font-sans text-footnote text-light-text-secondary dark:text-dark-text-secondary max-w-[260px] text-center">
          Find a coworker by name or company to start a chat.
        </Text>
      </View>
      <Pressable
        accessibilityRole="button"
        onPress={onNewChatPress}
        className="bg-light-accent-brand dark:bg-dark-accent-brand px-lg py-sm rounded-full hover:opacity-90 active:opacity-80"
      >
        <Text className="font-sans text-footnote font-semibold text-light-text-onAccent dark:text-dark-text-onAccent">
          Start a chat
        </Text>
      </Pressable>
    </View>
  );
}

// ── Utilities ───────────────────────────────────────────────────────

function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const hours = diff / (1000 * 60 * 60);
  if (hours < 24) {
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  }
  if (hours < 24 * 7) {
    return date.toLocaleDateString(undefined, { weekday: "short" });
  }
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}
